import { useEffect, useState } from 'react';
import { AllBreakpointsOf } from '../theme';
import { useWindowSize } from './useWindowSize';

/**
 * @title useCurrentBreakpoint
 * Hook to return the currently active breakpoint from the given
 * breakpoints object based on the window width.
 *
 * @returns currently active breakpoint name eg `xs`
 */
export function useCurrentBreakpoint<
  T extends number,
  B extends AllBreakpointsOf<T>,
  K extends keyof B
>(breakpoints: B): K {
  const windowWidth = useWindowSize();

  const [currentBreakpoint, setCurrentBreakpoint] = useState<K>();

  useEffect(() => {
    if (windowWidth) {
      const breakpointEntries = Object.entries(breakpoints);

      const largestBreakpointWithinWindow = breakpointEntries.reduce(
        (accumulatedLargest, [breakpointName, breakpointValue]) => {
          return windowWidth >= breakpointValue
            ? breakpointName
            : accumulatedLargest;
        },
        /**
         * start with `xs` breakpoint as default
         **/
        breakpointEntries[0][0]
      );
      setCurrentBreakpoint(largestBreakpointWithinWindow as K);
    }
    /**
     * linter is confusing types with actual JS variables and expects
     * `K` to be included in dependency array :(
     **/
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth, breakpoints]);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return currentBreakpoint!;
}
